/**
 * ConsultationCards Component - By John Bellew - Infinia.ie - last updated 11/07/2025
 * 
 * Displays consultation data in a card-based grid layout with interactive PDF modal functionality.
 * Each card shows consultation details including name, topic, status, category, and other metadata.
 * Cards can be clicked to open associated PDF documents in modal windows.
 * 
 * Features:
 * - Responsive card grid layout
 * - Search query highlighting
 * - Multiple PDF modal management
 * - Status badges (Live, Completed, etc.)
 * - Category-based color coding
 * - Tooltips for truncated content
 * - Click-to-open PDF functionality
 * 
 * @component
 * @param {Object} props - Component props
 * @param {Array} props.data - Array of consultation objects to display
 * @param {string} props.searchQuery - Search query string for highlighting matches
 * @returns {JSX.Element} The rendered consultation cards component
 * 
 * @example
 * ```jsx
 * <ConsultationCards 
 *   data={consultationData} 
 *   searchQuery="regulation" 
 * />
 * ```
 */

import React, { useState } from 'react';
import Tippy from '@tippyjs/react';
import 'tippy.js/dist/tippy.css';
import PDFModal from '../../modals/PDFModal';
import { truncateText, formatDate, normalizeText } from '../../utils/textUtils';
import './ConsultationCards.css';

const ConsultationCards = ({ data, searchQuery }) => {
    const [modals, setModals] = useState([]);

    /**
     * Get CSS color class for consultation category
     * 
     * Maps consultation categories to predefined color classes for consistent styling.
     * Performs case-insensitive matching and supports partial matches.
     * 
     * @param {string} category - The consultation category
     * @returns {string} CSS class name for the category color
     * 
     * @example
     * ```javascript
     * getCategoryColor('Consultation') // Returns 'consultation'
     * getCategoryColor('PAPER') // Returns 'paper'
     * getCategoryColor('unknown') // Returns 'light-gray'
     * ```
     */
    const getCategoryColor = (category) => {
        const colorMap = {
            'Consultation': 'consultation',
            'Paper': 'paper',
            'Submission': 'submission',
            'Response': 'coral',
            'Statement': 'dust-rose',
            'Guidance': 'cyan',
            'Policy': 'peach',
            'Regulation': 'sage',
            'Directive': 'purple',
            'Technical': 'teal',
            'Legal': 'indigo'
        };
        
        // Find matching category (case-insensitive)
        const normalizedCategory = category?.toLowerCase();
        const matchedKey = Object.keys(colorMap).find(key => 
            key.toLowerCase() === normalizedCategory || 
            normalizedCategory?.includes(key.toLowerCase())
        );
        
        return colorMap[matchedKey] || 'light-gray';
    };

    /**
     * Open a PDF modal or bring existing modal to front
     * 
     * Manages multiple PDF modals with proper z-index stacking. If a modal for the same
     * consultation already exists, it brings it to the front. Otherwise, creates a new modal
     * with proper positioning and z-index management.
     * 
     * @param {string|number} id - Unique identifier for the consultation
     * @param {string} title - Title to display in the modal header
     * @param {string} pdfUrl - URL of the PDF document to display
     * 
     * @example
     * ```javascript
     * openPDFModal('consultation-123', 'Consultation Title', 'https://example.com/doc.pdf')
     * ```
     */
    const openPDFModal = (id, title, pdfUrl, consultationData = null) => {
        const existingModalIndex = modals.findIndex((modal) => modal.id === id);
        const maxZIndex = Math.max(...modals.map((modal) => modal.zIndex), 0) + 1;

        if (existingModalIndex !== -1) {
            const updatedModals = [...modals];
            updatedModals[existingModalIndex].isOpen = true;
            updatedModals[existingModalIndex].zIndex = maxZIndex;
            setModals(updatedModals);
        } else {
            const newModal = {
                id,
                title,
                pdfUrl,
                consultationData,
                isOpen: true,
                top: `${15 + (modals.length * 2)}%`,
                left: `${30 + (modals.length * 2)}%`,
                zIndex: maxZIndex,
            };
            setModals((prevModals) => [...prevModals, newModal]);
        }
    };

    /**
     * Close a PDF modal by ID
     * 
     * Marks the specified modal as closed while maintaining it in the modals array
     * for potential reopening. The modal component handles the actual visibility.
     * 
     * @param {string|number} id - Unique identifier of the modal to close
     * 
     * @example
     * ```javascript
     * closePDFModal('consultation-123')
     * ```
     */
    const closePDFModal = (id) => {
        setModals((prevModals) =>
            prevModals.map((modal) =>
                modal.id === id ? { ...modal, isOpen: false } : modal
            )
        );
    };

    return (
        <section id="ConsultationCards" className="consultation-cards-section">
            {data.length === 0 ? (
                <div className="no-results">
                    <div className="no-results-content">
                        <i className="fas fa-search"></i>
                        <h3>No consultations found</h3>
                        <p>Try adjusting your search criteria or filters to see more results.</p>
                    </div>
                </div>
            ) : (
                <div className="consultation-cards-container">
                    <div className="consultation-cards-grid">
                        {data.map((item) => (
                            <div
                                key={item.id}
                                className="consultation-card"
                                onClick={() => item.link && openPDFModal(item.id, item.name, item.longFileName || item.link, item)}
                                style={{ cursor: item.link ? 'pointer' : 'default' }}
                            >
                                {item.status === 'Live' && (
                                    <>
                                    <div className="status-badge live">
                                        <span>LIVE</span>
                                        <div className="pulse-dot"></div>
                                        </div>
                                        <div className="consultation-card-date-header live-header">
                                            <div className="consultation-icon">
                                                <i className="fas fa-broadcast-tower"></i>
                                            </div>
                                            <div className="live-status-top">
                                                <span>LIVE</span>
                                            </div>
                                        </div>
                                    </>
                                )}

                                {item.status === 'Completed' && item.completed && (
                                    <div className="consultation-card-date-header">
                                        <div className="consultation-icon">
                                            <i className="fas fa-file-pdf"></i>
                                        </div>
                                        <div className="completion-date-top">
                                            <i className="fas fa-calendar-alt"></i>
                                            <span>{formatDate(item.completed)}</span>
                                        </div>
                                    </div>
                                )}

                                <div className="consultation-card-content">
                                    <Tippy content={normalizeText(item.name)}>
                                        <h3 className="consultation-card-title">
                                            <span className="title-text">
                                                {searchQuery.trim() ? (
                                                    <span dangerouslySetInnerHTML={{ 
                                                        __html: truncateText(normalizeText(item.name), 65).replace(
                                                            new RegExp(searchQuery.trim(), 'gi'), 
                                                            match => `<span class="highlight-match">${match}</span>`
                                                        )
                                                    }} />
                                                ) : (
                                                    truncateText(normalizeText(item.name), 65)
                                                )}
                                            </span>
                                        </h3>
                                    </Tippy>

                                    {item.topic && item.topic !== 'N/A' && item.topic.trim() && (
                                        <div className="consultation-card-summary">
                                            <Tippy content={normalizeText(item.topic)}>
                                                <p>
                                                    {searchQuery.trim() ? (
                                                        <span dangerouslySetInnerHTML={{ 
                                                            __html: truncateText(normalizeText(item.topic), 180).replace(
                                                                new RegExp(searchQuery.trim(), 'gi'), 
                                                                match => `<span class="highlight-match">${match}</span>`
                                                            )
                                                        }} />
                                                    ) : (
                                                        truncateText(normalizeText(item.topic), 180)
                                                    )}
                                                </p>
                                            </Tippy>
                                        </div>
                                    )}
                                </div>

                                <div className="consultation-card-footer">
                                    <div className="consultation-card-tags">
                                        {item.category && (
                                            <Tippy content={item.category} disabled={item.category.length <= 25}>
                                                <span className={`consultation-tag category-tag consultation-tag-${getCategoryColor(item.category)}`}>
                                                    {truncateText(item.category, 25)}
                                            </span>
                                            </Tippy>
                                        )}
                                        {item.status && item.status !== 'Live' && (
                                            <Tippy content={item.status} disabled={item.status.length <= 25}>
                                                <span className={`consultation-tag status-tag ${item.status === 'Completed' ? 'consultation-tag-completed' : 'consultation-tag-pending'}`}>
                                                    {truncateText(item.status, 25)}
                                            </span>
                                            </Tippy>
                                        )}
                                        {item.ib && item.ib !== 'N/A' && (
                                            <Tippy content={item.ib} disabled={item.ib.length <= 25}>
                                                <span className={`consultation-tag issuing-body-tag consultation-tag-${getCategoryColor(item.ib)}`}>
                                                    {truncateText(item.ib, 25)}
                                            </span>
                                            </Tippy>
                                        )}
                                        {item.wg && item.wg !== 'N/A' && (
                                            <Tippy content={item.wg} disabled={item.wg.length <= 25}>
                                                <span className={`consultation-tag working-group-tag consultation-tag-sage`}>
                                                    {truncateText(item.wg, 25)}
                                            </span>
                                            </Tippy>
                                        )}
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}
            
            {modals.map((modal) => (
                <PDFModal
                    key={modal.id}
                    title={modal.title}
                    url={modal.pdfUrl}
                    show={modal.isOpen}
                    onClose={(e) => {
                        e.stopPropagation();
                        closePDFModal(modal.id);
                    }}
                    allowFullscreen={true}
                    allowMove={true}
                    showLinkinToolBar={true}
                    style={{ zIndex: modal.zIndex }}
                    consultationData={modal.consultationData}
                />
            ))}
        </section>
    );
};

export default ConsultationCards;

